import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LinkIcon from '@mui/icons-material/Link';
import MailIcon from '@mui/icons-material/Mail';
import { Avatar, Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Contact from 'shared/db/services/Contact';
import { ContactModel } from 'shared/types/Contacts';
import { addAlphaToColor } from 'shared/utils/colors';

import { UserProfileIcon } from '@/assets/svg';
import { useImagesContext } from '@/context/ImagesContext';
import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { useDatabase } from '@/hooks';
import { useEntityImage } from '@/hooks/useEntityImage';
import { databaseValidation } from '@/services/databaseValidation';
import Logger from '@/services/logger';
import { dbServiceLogger } from '@/services/logger/logger';
import { OrganisationsAPI } from '@/services/networking/organisations';
import { Snackbar } from '@/services/toastNotifications';
import { t } from '@/services/translations/config';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';
import { formatUnixTimestamp } from '@/utils/date';
import { getImageDisplayUrl } from '@/utils/images';

import CancelAcceptedOrganisationInvitation from '../../CancelAcceptedOrganisationInvitation';
import CancelOrganisationInvitation from '../../CancelOrganisationInvitation';

import { Props } from './types';

const SentInvitationItem: React.FC<Props> = ({ invitation }) => {
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isInvitationCanceledModalOpen, setIsInvitationCanceledModalOpen] =
        useState(false);
    const [contact, setContact] = useState<ContactModel | null>(null);
    const { fetchMembers } = useOrganisationsContext();
    const { ImagesService } = useImagesContext();
    const { getDatabase } = useDatabase();

    const contactService = useMemo(
        () =>
            new Contact({
                database: getDatabase(),
                imageService: ImagesService,
                logger: dbServiceLogger,
                validation: databaseValidation,
            }),
        [getDatabase, ImagesService],
    );

    useEffect(() => {
        if (invitation.contact_id) {
            contactService.getByID(invitation.contact_id).then((contact) => {
                setContact(contact);
            });
        }
    }, [invitation, contactService]);

    const image = useEntityImage(invitation.contact_id ?? '');
    const avatarURL = getImageDisplayUrl(image) ?? '';

    const closeCancelModal = useCallback(() => {
        setIsCancelModalOpen(false);
    }, []);

    const openCancelModal = useCallback(() => {
        setIsCancelModalOpen(true);
    }, []);

    const handleCancelSuccess = useCallback(() => {
        closeCancelModal();
    }, [closeCancelModal]);

    const openCancelInvitationCanceledModal = useCallback(() => {
        setIsInvitationCanceledModalOpen(true);
    }, []);

    const handleCancelInvitationCanceledSuccess = useCallback(async () => {
        await fetchMembers();
        setIsInvitationCanceledModalOpen(false);
    }, [fetchMembers]);

    const cancelInvitation = useCallback(async () => {
        try {
            await OrganisationsAPI.cancelInvitation({
                invitationId: invitation.id,
            });
            await fetchMembers();
        } catch (err) {
            if (err?.response?.status === 404) {
                openCancelInvitationCanceledModal();
            } else {
                Logger.logError(err);

                Snackbar.showToastNotification({
                    message: t('App:Messages:something_went_wrong'),
                    options: {
                        variant: 'error',
                    },
                });
            }
        } finally {
            closeCancelModal();
        }
    }, [
        invitation.id,
        fetchMembers,
        closeCancelModal,
        openCancelInvitationCanceledModal,
    ]);

    const contactName = contact
        ? [contact?.firstName, contact?.lastName].filter(Boolean).join(' ')
        : invitation.email || '';

    return (
        <>
            {isCancelModalOpen && (
                <CancelOrganisationInvitation
                    avatarURL={avatarURL}
                    invitation={invitation}
                    onClose={closeCancelModal}
                    onCancel={cancelInvitation}
                    handleCancelSuccess={handleCancelSuccess}
                />
            )}

            {isInvitationCanceledModalOpen && (
                <CancelAcceptedOrganisationInvitation
                    name={contactName}
                    onCancel={handleCancelInvitationCanceledSuccess}
                />
            )}

            <Box
                component="li"
                key={invitation.id}
                sx={{
                    display: 'flex',
                    gap: '16px',
                    backgroundColor: COLOR.headerGrey,
                    borderTopLeftRadius: '32px',
                    borderBottomRightRadius: '8px',
                    borderTopRightRadius: '8px',
                    cursor: 'pointer',
                }}
                onClick={openCancelModal}
            >
                <Box sx={{ position: 'relative' }}>
                    <Avatar
                        sx={{
                            width: '64px',
                            height: '64px',
                            backgroundColor: COLOR.gainsboro,
                        }}
                        src={avatarURL}
                    >
                        <UserProfileIcon color={COLOR.mischka} />
                    </Avatar>

                    <Box
                        sx={{
                            position: 'absolute',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            top: 10,
                            right: -8,
                            width: 22,
                            height: 22,
                            borderRadius: '50px',
                            backgroundColor: ENTITY_COLOR.teams,
                        }}
                    >
                        <AccessTimeIcon
                            sx={{ color: COLOR.white, width: 16, height: 16 }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        py: '12px',
                    }}
                >
                    <Typography
                        fontSize={16}
                        fontWeight={700}
                        color={COLOR.black}
                    >
                        {contact
                            ? [contact.firstName, contact.lastName]
                                  .filter(Boolean)
                                  .join(' ')
                            : invitation.email
                            ? invitation.email
                            : t('TeamsPage:pending_invitation')}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}
                    >
                        {invitation.email ? (
                            <MailIcon
                                sx={{ width: '12px', color: COLOR.black }}
                            />
                        ) : (
                            <LinkIcon
                                sx={{ width: '12px', color: COLOR.black }}
                            />
                        )}
                        <Typography
                            fontSize={12}
                            color={addAlphaToColor(COLOR.ebonyClay, 0.4)}
                        >
                            {invitation.email
                                ? t('Organisation:sent_invitations_via', {
                                      email: invitation.email,
                                  })
                                : t('Organisation:sent_invitations_via_link')}
                        </Typography>
                    </Box>
                    <Typography
                        fontSize={10}
                        color={addAlphaToColor(COLOR.ebonyClay, 0.4)}
                    >
                        {invitation.email
                            ? t('Organisation:sent_on', {
                                  data: formatUnixTimestamp(
                                      invitation.created_at,
                                      'ddd MM-DD-YYYY h:mm a',
                                  ),
                              })
                            : t('Organisation:link_generated_on', {
                                  data: formatUnixTimestamp(
                                      invitation.created_at,
                                      'ddd MM-DD-YYYY h:mm a',
                                  ),
                              })}
                    </Typography>
                </Box>
            </Box>
        </>
    );
};

export default SentInvitationItem;
